import { Row } from "@tanstack/react-table";
import { FiltersInterface } from "config/consts";
import { useIsMobile, useIsTablet } from "hooks/useDeviceType";
import { useIsTradeHistoryReturned } from "hooks/useIsTradeHistoryReturned";
import { CSSProperties, useEffect, useMemo } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList as List } from "react-window";
import { TransactionDetail } from "types/history";
import { TableRow } from "./TableRow";

interface ActivityTableListProps {
  rows: Array<Row<TransactionDetail>>;
  confirmedFilters: FiltersInterface;
  virtualizedListRef: React.RefObject<List<typeof TableRow>>;
}

export const ActivityTableList = ({
  rows,
  confirmedFilters,
  virtualizedListRef,
}: ActivityTableListProps) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isTradeHistoryReturned = useIsTradeHistoryReturned();
  const Row = useMemo(() => getRow(rows), [rows]);

  // Do not memoize this function using useCallback; it may mess up the
  // calculation of row heights upon filtering or clearing all filters
  const getItemSize = (index: number) => {
    const { firstTxOfMonth } = getRowDetails(rows, index);
    const monthTitleHeight = isMobile
      ? ACITIVTY_MONTH_TITLE_HEIGHT_MOBILE
      : ACITIVTY_MONTH_TITLE_HEIGHT;
    const titleHeight = firstTxOfMonth ? monthTitleHeight : 0;

    if (isMobile) {
      return ACTIVITY_ROW_HEIGHT_MOBILE + titleHeight;
    }

    const row = rows[index];
    const sellTokenInfo = row.getValue<TransactionDetail["sellTokens"]>("sellTokens");
    const buyTokenInfo = row.getValue<TransactionDetail["buyTokens"]>("buyTokens");
    const maxBuyOrSellTokenCount = Math.max(
      Object.keys(sellTokenInfo).length,
      Object.keys(buyTokenInfo).length
    );

    // Hardcode row height for virtualized list
    // All rows with > 3 tokens stacked vertically adds an additional 4px for each extra token
    const txRowHeight = ACTIVITY_ROW_HEIGHT_BASE + (maxBuyOrSellTokenCount - 3) * 4;
    return txRowHeight + titleHeight;
  };

  // Force re-render as virtualized list does not auto reflect updates
  useEffect(() => {
    // Reset all rows after index 0
    virtualizedListRef.current?.resetAfterIndex(0, false);
  }, [isMobile, isTablet, confirmedFilters, virtualizedListRef]);

  if (!isTradeHistoryReturned) {
    return (
      <div>
        {rows.map((row, rowIndex) => {
          const { id, firstTxOfMonth, lastTxOfMonth } = getRowDetails(rows, rowIndex);
          return (
            <TableRow
              key={id}
              row={row}
              firstTxOfMonth={firstTxOfMonth}
              lastTxOfMonth={lastTxOfMonth}
              rowIndex={rowIndex}
              style={undefined}
            />
          );
        })}
      </div>
    );
  }
  return (
    <AutoSizer>
      {({ height, width }: { height: number; width: number }) => {
        return (
          <List
            ref={virtualizedListRef}
            height={height}
            width={width}
            itemCount={rows.length}
            itemSize={getItemSize}
          >
            {Row}
          </List>
        );
      }}
    </AutoSizer>
  );
};

// Helpers

export const ACITIVTY_MONTH_TITLE_HEIGHT = 33 + 12; // desktop & tablet: actual height + gap on top
export const ACITIVTY_MONTH_TITLE_HEIGHT_MOBILE = 31 + 10; // mobile: actual height + gap on top
const ACTIVITY_ROW_HEIGHT_BASE = 64;
const ACTIVITY_ROW_HEIGHT_MOBILE = 92;

interface RowProps {
  index: number;
  style: CSSProperties | undefined;
}

const getRow =
  (rows: ActivityTableListProps["rows"]) =>
  ({ index, style }: RowProps) => {
    const { id, row, firstTxOfMonth, lastTxOfMonth } = getRowDetails(rows, index);
    return (
      <TableRow
        key={id}
        row={row}
        firstTxOfMonth={firstTxOfMonth}
        lastTxOfMonth={lastTxOfMonth}
        rowIndex={index}
        style={style}
      />
    );
  };

const getRowDetails = (rows: ActivityTableListProps["rows"], index: number) => {
  const row = rows[index];
  const currMonth = row.original.timestamp.getMonth();
  const prevRow = rows[index - 1];
  const nextRow = rows[index + 1];
  const prevMonth = prevRow ? prevRow.original.timestamp.getMonth() : null;
  const nextMonth = nextRow ? nextRow.original.timestamp.getMonth() : null;
  const firstTxOfMonth = currMonth !== prevMonth;
  const lastTxOfMonth = currMonth !== nextMonth;
  const id = row.original.txHash;
  return { id, row, firstTxOfMonth, lastTxOfMonth };
};
